
import React, { useContext, useMemo, useEffect, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { Socio } from '../types';
import { supabase } from '../contexts/AppContext';
import Modal from '../components/Modal';
import EditProfile from '../components/EditProfile';

const PortalSocioScreen: React.FC = () => {
  const { user, movimientosPuntos, logout, socios } = useContext(AppContext);
  // Runtime sanity logs to detect invalid component types causing React error #310
  try {
    console.log('PortalSocio: Modal type:', typeof Modal, 'EditProfile type:', typeof EditProfile, 'supabase type:', typeof supabase);
  } catch (e) {
    console.warn('PortalSocio: runtime type logging failed', e);
  }
  const [socioActual, setSocioActual] = useState<Socio | null>(null);
  const [socioId, setSocioId] = useState<string | null>(null);
  const [ventaModalOpen, setVentaModalOpen] = useState(false);
  const [ventaDetalle, setVentaDetalle] = useState<any | null>(null);

  const openVentaModal = async (ventaId: number) => {
    try {
      setVentaDetalle(null);
      setVentaModalOpen(true);
      const { data, error } = await supabase
        .from('ventas')
        .select('*, items:venta_items(*, producto:productos(*))')
        .eq('id', ventaId)
        .single();
      if (error) throw error;
      setVentaDetalle(data);
    } catch (err: any) {
      console.error('Error fetching venta:', err);
      setVentaDetalle(null);
    }
  };

  const closeVentaModal = () => { setVentaDetalle(null); setVentaModalOpen(false); };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [localMovimientos, setLocalMovimientos] = useState<any[]>([]);

  useEffect(() => {
    // Primero intentamos derivar el socio desde el listado en memoria (`socios`) para
    // que el UI se actualice inmediatamente cuando cambiamos puntos en la app.
    const deriveFromContext = () => {
      if (!user || !user.email || !socios || socios.length === 0) return false;
      const s = socios.find(sc => sc.email === user.email || String(sc.id) === String(user.id));
      if (s) {
          const sanitized = JSON.parse(JSON.stringify(s));
          setSocioActual(sanitized);
          const newId = sanitized?.id ? String(sanitized.id) : null;
          setSocioId(newId);
          console.log('PortalSocio: deriveFromContext - socio encontrado, ID:', newId, 'email:', sanitized?.email);
          // Fetch movimientos immediately without useEffect
          if (newId) {
            console.log('PortalSocio: iniciando fetch de movimientos para socio_id:', newId);
            supabase
              .from('movimientos_puntos')
              .select('*')
              .eq('socio_id', newId)
              .order('creado_en', { ascending: false })
              .then(
                ({ data, error }) => {
                  if (error) {
                    console.log('PortalSocio: fetch movimientos error ->', error);
                    setLocalMovimientos([]);
                  } else {
                    console.log('PortalSocio: fetch movimientos SUCCESS - items:', Array.isArray(data) ? data.length : typeof data, 'data:', data);
                    setLocalMovimientos(data || []);
                  }
                },
                (err) => {
                  console.error('PortalSocio: fetch movimientos failed', err);
                  setLocalMovimientos([]);
                }
              );
          }
        setLoading(false);
        return true;
      }
      return false;
    };

    const fetchSocioProfile = async () => {
      if (!user || !user.email) {
        setLoading(false);
        return;
      }

      // Si pudimos derivar desde contexto, evitamos re-fetch
      if (deriveFromContext()) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error: dbError } = await supabase
          .from('socios')
          .select('*')
          .eq('email', user.email)
          .single();

        if (dbError) {
          if (dbError.code === 'PGRST116') {
             throw new Error("No se encontró un perfil de socio único asociado a tu cuenta. Puede que tus datos no estén registrados o que haya una duplicidad.");
          }
          throw dbError;
        }

          const sanitized = JSON.parse(JSON.stringify(data));
          setSocioActual(sanitized);
          const newId = sanitized?.id ? String(sanitized.id) : null;
          setSocioId(newId);
          console.log('PortalSocio: fetchSocioProfile - socio obtenido de DB, ID:', newId, 'email:', sanitized?.email);
          // Fetch movimientos immediately without useEffect
          if (newId) {
            console.log('PortalSocio: iniciando fetch de movimientos para socio_id:', newId);
            supabase
              .from('movimientos_puntos')
              .select('*')
              .eq('socio_id', newId)
              .order('creado_en', { ascending: false })
              .then(
                ({ data, error }) => {
                  if (error) {
                    console.log('PortalSocio: fetch movimientos error ->', error);
                    setLocalMovimientos([]);
                  } else {
                    console.log('PortalSocio: fetch movimientos SUCCESS - items:', Array.isArray(data) ? data.length : typeof data, 'data:', data);
                    setLocalMovimientos(data || []);
                  }
                },
                (err) => {
                  console.error('PortalSocio: fetch movimientos failed', err);
                  setLocalMovimientos([]);
                }
              );
          }

      } catch (err: any) {
        console.error("Error fetching socio profile:", err);
        setError(err.message || "Ocurrió un error al cargar tu perfil.");
      } finally {
        setLoading(false);
      }
    };

    fetchSocioProfile();
  }, [user?.email, socios?.length]);


  const handleLogout = async () => {
    await logout();
  }

  if (loading) {
    return (
       <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !socioActual) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center w-full max-w-md">
          <p className="text-red-600 font-semibold mb-4">{error || "No se encontró un perfil de socio asociado a tu cuenta."}</p>
          <p className="text-gray-600 text-sm mb-6">Por favor, contactá a la administración del club para verificar tus datos.</p>
          <button onClick={handleLogout} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
            Salir
          </button>
        </div>
      </div>
    );
  }

  // More runtime inspection right before rendering main UI
  try {
    console.log('PortalSocio: socioActual snapshot:', socioActual);
    console.log('PortalSocio: socioActual types', { nombre: typeof socioActual?.nombre, puntosType: typeof socioActual?.puntos, idType: typeof socioActual?.id });
    // avoid referencing `misMovimientos` before it's declared (causes TDZ ReferenceError)
    const safeMisCount = Array.isArray(movimientosPuntos) ? movimientosPuntos.filter(m => String(m?.socio_id) === String(socioActual?.id)).length : 0;
    console.log('PortalSocio: misMovimientos count (computed)', safeMisCount, 'localMovimientos count', (localMovimientos || []).length);
  } catch (e) {
    console.warn('PortalSocio: pre-render logging failed', e);
  }

  // (Hotfix removed) continue rendering full UI

  const misMovimientos = (movimientosPuntos || [])
    .filter(m => String(m?.socio_id) === String(socioActual?.id))
    .sort((a,b) => new Date(b?.creado_en).getTime() - new Date(a?.creado_en).getTime());

  // Safe helper for venta items to avoid runtime errors when data is malformed
  const ventaItems = Array.isArray(ventaDetalle?.items) ? ventaDetalle.items : [];

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <header className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Hola, {socioActual.nombre}</h1>
            <div className="flex gap-2">
              <button onClick={() => setShowEdit(true)} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm">
                Editar perfil
              </button>
              <button onClick={handleLogout} className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 text-sm">
                Salir
              </button>
            </div>
        </header>

        <div className="bg-blue-600 text-white p-8 rounded-xl shadow-lg mb-8 text-center">
          <p className="text-lg opacity-80">Tus Puntos Disponibles</p>
          <p className="text-6xl font-bold tracking-tight">{socioActual.puntos}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Mi Historial de Puntos</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b-2 border-gray-200">
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase">Fecha</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase">Descripción</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase">Puntos</th>
                </tr>
              </thead>
              <tbody>
                {(localMovimientos.length > 0 ? localMovimientos : misMovimientos).map(mov => {
                  const match = /venta_id[:\s]*([0-9]+)/i.exec(String(mov.descripcion || ''));
                  return (
                  <tr key={mov.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">{new Date(mov.creado_en).toLocaleDateString('es-AR')}</td>
                    <td className="py-3 px-4 text-gray-700 capitalize">
                      {match ? (
                        <button onClick={() => openVentaModal(Number(match[1]))} className="text-blue-600 underline">
                          {mov.descripcion.replace(/\(venta_id:?\s*\d+\)/i, `(venta #${match[1]})`)}
                        </button>
                      ) : mov.descripcion}
                    </td>
                    <td className={`py-3 px-4 font-bold ${mov.puntos > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {mov.puntos > 0 ? `+${mov.puntos}` : mov.puntos}
                    </td>
                  </tr>
                )})}
                {misMovimientos.length === 0 && (
                    <tr>
                        <td colSpan={3} className="text-center py-8 text-gray-500">No tenés movimientos todavía.</td>
                    </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Editar mi perfil">
        <EditProfile socio={socioActual} onSaved={(s) => { setSocioActual(s); setShowEdit(false); }} />
      </Modal>

      <Modal isOpen={ventaModalOpen} onClose={closeVentaModal} title={ventaDetalle ? `Venta #${ventaDetalle.id}` : 'Cargando venta...'}>
        {ventaDetalle ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">Cajero: {ventaDetalle.creado_por}</div>
            <div className="text-sm text-gray-600">Medio de pago: {ventaDetalle.medio_de_pago}</div>
            <div className="pt-2">
              <p className="text-sm font-medium text-gray-600">Items:</p>
              <ul className="list-disc list-inside text-sm text-gray-600 mt-1 pl-2">
                {ventaItems.map((it: any) => (
                  <li key={it?.id ?? `${it?.producto_id}-${Math.random()}`}>{it?.cantidad} x {it?.producto?.nombre || 'Producto'} - ${(Number(it?.cantidad || 0) * Number(it?.precio_unitario || 0)).toLocaleString('es-AR')}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-between text-sm text-gray-700 font-medium mt-2">
              <span>Total antes del canje</span>
              <span>${ventaItems.reduce((s: number, it: any) => s + (Number(it?.cantidad || 0) * Number(it?.precio_unitario || 0)), 0).toLocaleString('es-AR')}</span>
            </div>
            {Number(ventaDetalle.puntos_canjeados || 0) > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>Puntos canjeados</span>
                <span>-${Number(ventaDetalle.puntos_canjeados || 0).toLocaleString('es-AR')}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-gray-900 font-bold">
              <span>Total final</span>
              <span>${Number(ventaDetalle.total || 0).toLocaleString('es-AR')}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">Cargando...</div>
        )}
      </Modal>
    </div>
  );
};

export default PortalSocioScreen;